import {
  LANDING_ZONE,
  START_SAFE_PLATFORMS,
  POWER_UP_TYPES,
  backgroundThemes,
} from '../config/gameConfig.js';
import {
  AIR_ENEMY_KINDS,
  ENEMY_BEHAVIOR,
  PATROL_ENEMY_KINDS,
  STATIC_OBSTACLE_KINDS,
} from '../config/enemyConfig.js';
import { sprites } from '../config/assets.js';
import { random, clamp } from '../utils/math.js';
import {
  state,
  player,
  platforms,
  peanuts,
  hearts,
  enemies,
  decor,
  bursts,
  powerUps,
  enemyProjectiles,
  backgroundProps,
} from './state.js';
import { currentDifficulty } from './difficulty.js';

export function spawnEnemy(platform, enemyX, difficulty) {
  const canFly =
    platform.index > START_SAFE_PLATFORMS + 2 && Math.random() < 0.28 + difficulty * 0.28;
  if (canFly) {
    const kind = chooseAirEnemyKind(difficulty);
    const config = ENEMY_BEHAVIOR[kind] || ENEMY_BEHAVIOR.swooperBat;
    const usesConfiguredWave = kind === 'swooperBat' || kind === 'leafMoth' || kind === 'leafWasp';
    const baseY = usesConfiguredWave
      ? platform.y - random(config.baseYOffset[0], config.baseYOffset[1])
      : platform.y - random(112, 154);
    enemies.push({
      kind,
      x: platform.x + platform.width + random(18, 60),
      y: baseY,
      baseY,
      width:
        kind === 'swooperBat'
          ? config.width
          : kind === 'leafMoth' || kind === 'leafWasp'
            ? config.width
            : kind === 'acornBat'
              ? 70
              : 68,
      height:
        kind === 'swooperBat'
          ? config.height
          : kind === 'leafMoth' || kind === 'leafWasp'
            ? config.height
            : kind === 'acornBat'
              ? 56
              : 58,
      vx:
        kind === 'swooperBat'
          ? random(config.vx[0], config.vx[1]) + difficulty * config.difficultySpeed
          : kind === 'leafMoth' || kind === 'leafWasp'
            ? random(config.vx[0], config.vx[1]) + difficulty * config.difficultySpeed
            : random(28, 48) + difficulty * (kind === 'acornBat' ? 20 : 18),
      phase: random(0, Math.PI * 2),
      diveAmplitude:
        kind === 'swooperBat'
          ? random(config.diveAmplitude[0], config.diveAmplitude[1])
          : kind === 'leafMoth' || kind === 'leafWasp'
            ? random(config.verticalAmplitude[0], config.verticalAmplitude[1])
            : undefined,
      waveSpeed:
        kind === 'leafMoth' || kind === 'leafWasp'
          ? random(config.waveSpeed[0], config.waveSpeed[1])
          : undefined,
    });
    return;
  }

  const roll = Math.random();
  const obstacleRollStart = 1 - (state.level.obstacleChance ?? 0.08);
  if (difficulty > 0.52 && roll < 0.09) {
    const config = ENEMY_BEHAVIOR.crystalSnail;
    enemies.push({
      kind: 'crystalSnail',
      x: enemyX,
      y: platform.y - config.yOffset,
      width: config.width,
      height: config.height,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - config.width,
      platformId: platform.id,
      yOffset: config.yOffset,
      patrolSpeed:
        random(config.patrolSpeed[0], config.patrolSpeed[1]) + difficulty * config.difficultySpeed,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
    });
  } else if (difficulty > 0.34 && roll < 0.13) {
    const config = ENEMY_BEHAVIOR.fireSalamander;
    enemies.push({
      kind: 'fireSalamander',
      x: enemyX,
      y: platform.y - config.yOffset,
      width: config.width,
      height: config.height,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - config.width,
      platformId: platform.id,
      yOffset: config.yOffset,
      patrolSpeed:
        random(config.patrolSpeed[0], config.patrolSpeed[1]) + difficulty * config.difficultySpeed,
      direction: -1,
      phase: random(0, Math.PI * 2),
      fireCooldown: random(config.fireCooldown[0], config.fireCooldown[1]),
      projectileSpeed:
        random(config.projectileSpeed[0], config.projectileSpeed[1]) + difficulty * 28,
    });
  } else if (difficulty > 0.24 && roll < 0.28) {
    const config = ENEMY_BEHAVIOR.turnipHopper;
    enemies.push({
      kind: 'turnipHopper',
      x: enemyX,
      y: platform.y - config.yOffset,
      width: config.width,
      height: config.height,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - config.width,
      platformId: platform.id,
      yOffset: config.yOffset,
      patrolSpeed:
        random(config.patrolSpeed[0], config.patrolSpeed[1]) + difficulty * config.difficultySpeed,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
      hopHeight: random(config.hopHeight[0], config.hopHeight[1]),
      hopSpeed: random(config.hopSpeed[0], config.hopSpeed[1]),
    });
  } else if (difficulty > 0.44 && roll < 0.39) {
    const config = ENEMY_BEHAVIOR.pineconeCharger;
    enemies.push({
      kind: 'pineconeCharger',
      x: enemyX,
      y: platform.y - config.yOffset,
      width: config.width,
      height: config.height,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - config.width,
      platformId: platform.id,
      yOffset: config.yOffset,
      patrolSpeed:
        random(config.patrolSpeed[0], config.patrolSpeed[1]) + difficulty * config.difficultySpeed,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
      dashCooldown: random(config.dashCooldown[0], config.dashCooldown[1]),
      dashTimer: 0,
    });
  } else if (roll < 0.53) {
    enemies.push({
      kind: 'ground',
      x: enemyX,
      y: platform.y - 40,
      width: 48,
      height: 37,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - 48,
      platformId: platform.id,
      yOffset: 40,
      patrolSpeed: random(18, 32) + difficulty * 16,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
    });
  } else if (roll < 0.65) {
    enemies.push({
      kind: 'mushroomHopper',
      x: enemyX,
      y: platform.y - 44,
      width: 54,
      height: 40,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - 54,
      platformId: platform.id,
      yOffset: 44,
      patrolSpeed: random(16, 28) + difficulty * 14,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
    });
  } else if (roll < 0.77) {
    enemies.push({
      kind: 'enemy',
      x: enemyX,
      y: platform.y - 42,
      width: 48,
      height: 39,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - 48,
      platformId: platform.id,
      yOffset: 42,
      patrolSpeed: random(14, 26) + difficulty * 12,
      direction: Math.random() < 0.5 ? -1 : 1,
    });
  } else if (roll < 0.8) {
    enemies.push({
      kind: 'chestnut',
      x: enemyX,
      y: platform.y - 42,
      width: 50,
      height: 39,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - 50,
      platformId: platform.id,
      yOffset: 42,
      patrolSpeed: random(22, 36) + difficulty * 18,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
    });
  } else if (roll < obstacleRollStart) {
    const config = ENEMY_BEHAVIOR.rollingChestnut;
    enemies.push({
      kind: 'rollingChestnut',
      x: enemyX,
      y: platform.y - config.yOffset,
      width: config.width,
      height: config.height,
      platformLeft: platform.x + LANDING_ZONE,
      platformRight: platform.x + platform.width - LANDING_ZONE - config.width,
      platformId: platform.id,
      yOffset: config.yOffset,
      patrolSpeed:
        random(config.patrolSpeed[0], config.patrolSpeed[1]) + difficulty * config.difficultySpeed,
      direction: Math.random() < 0.5 ? -1 : 1,
      phase: random(0, Math.PI * 2),
      dashCooldown: random(config.dashCooldown[0], config.dashCooldown[1]),
      dashTimer: 0,
    });
  } else {
    const kind = chooseStaticObstacleKind();
    enemies.push({
      kind,
      x: enemyX,
      y: platform.y - 48,
      width: 44,
      height: 44,
      platformId: platform.id,
      yOffset: 48,
    });
  }
}

function chooseAirEnemyKind(difficulty) {
  const roll = Math.random();
  if (difficulty > 0.45 && roll < 0.16) return 'leafWasp';
  if (difficulty > 0.36 && roll < 0.28) return 'swooperBat';
  if (difficulty > 0.28 && roll < 0.52) return 'leafMoth';
  return roll < 0.72 ? 'acornBat' : 'flying';
}

function chooseStaticObstacleKind() {
  const candidates = state.level.obstacleKinds || ['thistle'];
  const kind = candidates[Math.floor(random(0, candidates.length))];
  return STATIC_OBSTACLE_KINDS.includes(kind) ? kind : 'thistle';
}

export function addTutorialEnemy(platform, kind) {
  const width = kind === 'chestnut' ? 50 : 48;
  const height = kind === 'chestnut' ? 39 : 37;
  const yOffset = kind === 'chestnut' ? 42 : 40;

  enemies.push({
    kind,
    x: platform.x + platform.width * 0.56,
    y: platform.y - yOffset,
    width,
    height,
    platformLeft: platform.x + LANDING_ZONE,
    platformRight: platform.x + platform.width - LANDING_ZONE - width,
    platformId: platform.id,
    yOffset,
    patrolSpeed: 12,
    direction: -1,
    phase: random(0, Math.PI * 2),
  });
}

export function spawnTutorialItems(platform, spec) {
  for (let index = 0; index < (spec.peanuts || 0); index += 1) {
    const yOffset = 78;
    peanuts.push({
      x: platform.x + 72 + index * 44,
      y: platform.y - yOffset,
      size: 28,
      taken: false,
      bob: random(0, Math.PI * 2),
      platformId: platform.id,
      yOffset,
    });
  }

  if (spec.heart) {
    const yOffset = 112;
    hearts.push({
      x: platform.x + platform.width * 0.55,
      y: platform.y - yOffset,
      size: 32,
      taken: false,
      bob: random(0, Math.PI * 2),
      platformId: platform.id,
      yOffset,
    });
  }

  if (spec.enemy) {
    addTutorialEnemy(platform, spec.enemy);
  }
}

export function spawnPowerUp(platform, type, ratio = 0.5) {
  const def = POWER_UP_TYPES[type];
  if (!platform || !def) return;
  const size = 34;
  const x = clamp(
    platform.x + platform.width * ratio,
    platform.x + 46,
    platform.x + platform.width - 46,
  );
  const yOffset = 92;
  powerUps.push({
    type,
    x,
    y: platform.y - yOffset,
    size,
    taken: false,
    bob: random(0, Math.PI * 2),
    platformId: platform.id,
    yOffset,
    pulse: 0,
  });
}

export function maybeSpawnPowerUp(platform) {
  if (
    !platform ||
    state.level?.tutorial ||
    platform.index <= START_SAFE_PLATFORMS + 1 ||
    platform.width < 210
  )
    return;
  const difficulty = currentDifficulty();
  const chance = 0.075 + difficulty * 0.055;
  if (Math.random() > chance) return;
  const types = ['jumps', 'speed', 'invincible'];
  spawnPowerUp(platform, types[Math.floor(random(0, types.length))], random(0.34, 0.72));
}

export function createBackgroundProps() {
  const props = [];
  const theme = backgroundThemes[state.level.backgroundSet] || backgroundThemes.meadow;
  const candidates = theme.props;
  for (let index = 0; index < 10; index += 1) {
    const sprite = candidates[index % candidates.length];
    const lane = sprites.background[sprite].lane;
    const spacing = lane === 'sky' ? random(230, 340) : random(360, 520);
    props.push({
      sprite,
      lane,
      x: index * spacing + random(20, 130),
      size: lane === 'sky' ? random(74, 120) : random(105, 155),
      speed: lane === 'sky' ? random(0.06, 0.12) : random(0.11, 0.17),
    });
  }
  return props;
}

export function spawnBackgroundProp(initial = false) {
  const theme = backgroundThemes[state.level.backgroundSet] || backgroundThemes.meadow;
  if (!theme.props.length) return;
  const sprite = theme.props[Math.floor(random(0, theme.props.length))];
  const lane = sprites.background[sprite].lane;
  const x = initial ? random(0, state.width) : state.width + random(50, 150);
  backgroundProps.push({
    sprite,
    lane,
    x,
    size: lane === 'sky' ? random(74, 120) : random(105, 155),
    speed: lane === 'sky' ? random(0.06, 0.12) : random(0.11, 0.17),
  });
}

export function updateEntities(dt) {
  enemies.forEach((enemy) => {
    if (enemy.kind === 'rollingChestnut') updateRollingChestnut(enemy, dt);
    else if (enemy.kind === 'turnipHopper') updateTurnipHopper(enemy, dt);
    else if (enemy.kind === 'fireSalamander') updateFireSalamander(enemy, dt);
    else if (enemy.kind === 'pineconeCharger') updatePineconeCharger(enemy, dt);
    else if (STATIC_OBSTACLE_KINDS.includes(enemy.kind)) return;
    else if (PATROL_ENEMY_KINDS.includes(enemy.kind)) updatePatrolEnemy(enemy, dt);
    else if (AIR_ENEMY_KINDS.includes(enemy.kind)) {
      enemy.x -= (enemy.vx || 65) * dt;
      if (enemy.kind === 'swooperBat') {
        const dive = Math.max(
          0,
          Math.sin(state.time * ENEMY_BEHAVIOR.swooperBat.waveSpeed + enemy.phase),
        );
        enemy.y = enemy.baseY + dive * (enemy.diveAmplitude || 46);
      } else if (enemy.kind === 'leafMoth' || enemy.kind === 'leafWasp') {
        const wave = Math.sin(state.time * (enemy.waveSpeed || 2.6) + enemy.phase);
        enemy.y = enemy.baseY + wave * (enemy.diveAmplitude || 64);
      } else {
        enemy.y = enemy.baseY + Math.sin(state.time * 4.2 + enemy.phase) * 16;
      }
    }
  });
  enemyProjectiles.forEach((projectile) => {
    projectile.x += projectile.vx * dt;
    projectile.y += projectile.vy * dt + Math.sin(state.time * 9 + projectile.phase) * 6 * dt;
    projectile.life -= dt;
  });
  bursts.forEach((burst) => {
    burst.life -= dt;
    burst.y -= 42 * dt;
    if (burst.scale !== undefined) burst.scale += 1.8 * dt;
  });
  powerUps.forEach((p) => {
    p.pulse += dt * 6;
  });
}

function updateTurnipHopper(enemy, dt) {
  const config = ENEMY_BEHAVIOR.turnipHopper;
  enemy.phase += enemy.hopSpeed * dt;
  const hop = Math.max(0, Math.sin(enemy.phase));
  updatePatrolEnemy(enemy, dt, enemy.patrolSpeed * hop * config.airSpeedBoost);
  enemy.y -= hop * enemy.hopHeight;
}

function updateFireSalamander(enemy, dt) {
  updatePatrolEnemy(enemy, dt);
  enemy.fireCooldown -= dt;
  if (enemy.fireCooldown > 0 || enemy.x < -20 || enemy.x > state.width + 80) return;

  const config = ENEMY_BEHAVIOR.fireSalamander;
  enemy.fireCooldown = random(config.fireCooldown[0], config.fireCooldown[1]);
  const centerY = enemy.y + enemy.height * 0.42;
  const targetY = clamp(player.y + player.height * 0.5, centerY - 40, centerY + 40);
  enemyProjectiles.push({
    kind: 'fireball',
    x: enemy.x - 28,
    y: centerY - 13,
    width: 42,
    height: 26,
    vx: -enemy.projectileSpeed,
    vy: (targetY - centerY) * 0.8,
    phase: random(0, Math.PI * 2),
    life: 3.2,
  });
}

function updatePineconeCharger(enemy, dt) {
  const config = ENEMY_BEHAVIOR.pineconeCharger;
  enemy.dashCooldown -= dt;
  enemy.dashTimer = Math.max(0, enemy.dashTimer - dt);
  if (enemy.dashCooldown <= 0) {
    enemy.dashTimer = config.dashDuration;
    enemy.dashCooldown = random(config.dashCooldown[0], config.dashCooldown[1]);
  }
  updatePatrolEnemy(enemy, dt, enemy.dashTimer > 0 ? config.dashBoost : 0);
  if (enemy.dashTimer > 0) enemy.y -= Math.sin(state.time * 24 + enemy.phase) * config.hopHeight;
}

function updateRollingChestnut(enemy, dt) {
  const config = ENEMY_BEHAVIOR.rollingChestnut;
  enemy.dashCooldown -= dt;
  enemy.dashTimer = Math.max(0, enemy.dashTimer - dt);
  if (enemy.dashCooldown <= 0) {
    enemy.dashTimer = config.dashDuration;
    enemy.dashCooldown = random(config.dashCooldown[0], config.dashCooldown[1]);
  }
  updatePatrolEnemy(enemy, dt, enemy.dashTimer > 0 ? config.dashBoost : 0);
}

function updatePatrolEnemy(enemy, dt, speedBoost = 0) {
  if (
    enemy.platformLeft === undefined ||
    enemy.platformRight === undefined ||
    enemy.platformRight <= enemy.platformLeft
  ) {
    return;
  }
  enemy.x += enemy.direction * (enemy.patrolSpeed + speedBoost) * dt;
  if (enemy.x <= enemy.platformLeft) {
    enemy.x = enemy.platformLeft;
    enemy.direction = 1;
  } else if (enemy.x >= enemy.platformRight) {
    enemy.x = enemy.platformRight;
    enemy.direction = -1;
  }
}

export function pruneEntities() {
  const limit = -120;
  const p = platforms;
  p.splice(0, p.length, ...p.filter((item) => item.x + item.width > limit));
  peanuts.splice(0, peanuts.length, ...peanuts.filter((item) => item.x > limit && !item.taken));
  hearts.splice(0, hearts.length, ...hearts.filter((item) => item.x > limit && !item.taken));
  powerUps.splice(0, powerUps.length, ...powerUps.filter((item) => item.x > limit && !item.taken));
  enemies.splice(0, enemies.length, ...enemies.filter((e) => e.x > limit - 40 && !e.defeated));
  enemyProjectiles.splice(
    0,
    enemyProjectiles.length,
    ...enemyProjectiles.filter((p) => p.x + p.width > limit && p.life > 0),
  );
  decor.splice(0, decor.length, ...decor.filter((d) => d.x > limit));
  bursts.splice(0, bursts.length, ...bursts.filter((b) => b.life > 0));
}
